import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Form,
  Input,
  Layout,
  Result,
  Skeleton,
  Table,
  notification,
  theme,
} from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import {
  apiInvitationLinkList,
  apiCreateInvitationLink,
  apiEditInvitationLink,
  apiDeleteInvitationLink,
} from "../../../api/invitationLink";
import InvitationLinkModal from "./InvitationLinkModal/InvitationLinkModal";
import "./Accreditation.scss";

const { Content } = Layout;

const Accreditation = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [linkInfo, setLinkInfo] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const searchTimeoutRef = useRef(null);

  const navigate = useNavigate();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleListLinks = async (page = 1) => {
    setLoading(true);
    try {
      const json = await apiInvitationLinkList({ page });
      setFilteredData(json.results || []);
      setPagination((prev) => ({
        ...prev,
        total: json.count,
        current: page,
      }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setActiveError(true);
      if (error.status === 400) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao listar os links de convite!",
          placement: "topRight",
        });
        setErrorStatus("404");
      } else if (error.status === 500) {
        notification.error({
          message: "Erro",
          description:
            "Ocorreu um erro no servidor ao listar os links. Por favor, tente novamente mais tarde.",
          placement: "topRight",
        });
        setErrorStatus("500");
      }
    }
  };

  useEffect(() => {
    handleListLinks(pagination.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const performSearch = useCallback(async (value) => {
    setLoading(true);
    try {
      if (value === "") {
        await handleListLinks(1);
      } else {
        const json = await apiInvitationLinkList({ page: 1, search: value });
        setFilteredData(json.results || []);
        setPagination((prev) => ({
          ...prev,
          total: json.count,
          current: 1,
        }));
      }
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar os dados.",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 500);
  };

  const onChange = (paginationConfig) => {
    handleListLinks(paginationConfig.current);
  };

  const showModal = (info = null) => {
    setLinkInfo(info);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setLinkInfo(null);
  };

  const handleOk = async (payload) => {
    try {
      setLoadingAction(true);
      // Remove null values for create (POST)
      const cleanedPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v != null)
      );
      await apiCreateInvitationLink(cleanedPayload);
      notification.success({ message: "Link de convite criado com sucesso!" });
      form.resetFields();
      setIsModalVisible(false);
      handleListLinks(1);
    } catch (error) {
      if (error.status === 400) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao criar o link. Verifique as permissões.",
          placement: "topRight",
        });
      } else if (error.status === 500) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro no servidor. Por favor, tente novamente.",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Erro",
          description: "Erro ao criar o link de convite.",
          placement: "topRight",
        });
      }
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEdit = async (id, payload) => {
    try {
      setLoadingAction(true);
      // Send all values including nulls (PUT request replaces the entire resource)
      await apiEditInvitationLink(id, payload);
      notification.success({ message: "Link de convite atualizado com sucesso!" });
      form.resetFields();
      setIsModalVisible(false);
      setLinkInfo(null);
      handleListLinks(pagination.current);
    } catch (error) {
      if (error.status === 400) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao atualizar o link. Verifique as permissões.",
          placement: "topRight",
        });
      } else if (!error.errorFields) {
        notification.error({
          message: "Erro",
          description: "Erro ao atualizar o link de convite.",
          placement: "topRight",
        });
      }
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoadingAction(true);
      await apiDeleteInvitationLink(id);
      notification.success({ message: "Link de convite excluído com sucesso!" });
      form.resetFields();
      setIsModalVisible(false);
      setLinkInfo(null);
      handleListLinks(1);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Houve um erro ao excluir o link de convite.",
        placement: "topRight",
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCopyUrl = (shortId) => {
    const url = `${window.location.origin}/register/${shortId}`;
    navigator.clipboard.writeText(url).then(() => {
      notification.success({
        message: "Sucesso",
        description: "Link copiado com sucesso!",
        placement: "topRight",
      });
    });
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Link",
      key: "copy_link",
      render: (_, record) => (
        <button
          onClick={() => handleCopyUrl(record.short_id)}
          className="link-button"
        >
          <LinkOutlined />
        </button>
      ),
    },
    {
      title: "Agente",
      dataIndex: ["agent", "display_name"],
      key: "agent",
      render: (v) => v || "-",
    },
    {
      title: "Dist. N1",
      dataIndex: ["n1_distributor", "display_name"],
      key: "n1",
      render: (v) => v || "-",
    },
    {
      title: "Dist. N2",
      dataIndex: ["n2_distributor", "display_name"],
      key: "n2",
      render: (v) => v || "-",
    },
    {
      title: "Plano",
      dataIndex: ["plan", "name"],
      key: "plan",
      render: (v) => v || "-",
    },
    {
      title: "Criado em",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "-"),
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => (
        <Link onClick={() => showModal(record)}>Editar</Link>
      ),
    },
  ];

  const locale = {
    emptyText: <Empty description="Nenhum link de convite encontrado" />,
  };

  return (
    <div className="Accreditation">
      {activeError ? (
        renderError(errorStatus)
      ) : (
        <Layout style={{ padding: "1.25rem" }}>
          <Breadcrumb style={{ margin: "16px 0" }}>
            <div className="header-container">
              <h2 className="title-page">Credenciamento</h2>
            </div>
            <Divider />
            <Breadcrumb.Item>Credenciamento</Breadcrumb.Item>
            <Breadcrumb.Item>Links de Convite</Breadcrumb.Item>
          </Breadcrumb>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <div className="box-divider">
              <h3 className="subtitle-page">Links de Convite</h3>
              <Button onClick={() => showModal()} type="primary">
                Criar Link
              </Button>
            </div>
            <Divider />
            <Input.Search
              placeholder="Pesquisar"
              allowClear
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ marginBottom: 16, maxWidth: "300px" }}
            />
            {loading ? (
              <Skeleton active />
            ) : (
              <Table
                columns={columns}
                locale={locale}
                dataSource={filteredData.map((item) => ({
                  ...item,
                  key: item.id,
                }))}
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  showSizeChanger: false,
                }}
                bordered
                onChange={onChange}
              />
            )}
          </Content>
        </Layout>
      )}
      <InvitationLinkModal
        isModalVisible={isModalVisible}
        handleOk={handleOk}
        handleCancel={handleCancel}
        form={form}
        loading={loadingAction}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        linkInfo={linkInfo}
      />
    </div>
  );
};

export default Accreditation;
